/**
 * Recurrencia de eventos del calendario (ORR-383).
 *
 * La serie se guarda como UNA fila con `regla_recurrencia` en formato iCal RRULE, y el calendario
 * expande las ocurrencias al dibujar. Mismo patrón que las tareas recurrentes de ORR-331: sin
 * materializar filas, para no tener que sincronizarlas cuando la serie cambia.
 *
 * RRULE y no un formato propio porque Google Calendar acepta la misma cadena en `recurrence[]`:
 * lo que se guarda es lo que se manda, sin traducción que pueda divergir.
 */

/**
 * NADA de aritmética local acá, y no es estilo: `eventos_calendario.fecha_inicio` es `timestamptz`
 * pero el sistema la trata como HORA DE PARED — el front la escribe sin offset y PostgREST la
 * devuelve con `+00:00`, que no es un instante UTC real (ver `src/lib/fechaEvento.ts`). Los
 * componentes **UTC** de ese Date son lo que ve la persona, así que se opera con `getUTC*`/`setUTC*`
 * y con milisegundos. Con `getDate()` local, un evento de medianoche en Argentina cae el día
 * anterior y la serie se ancla mal.
 */

export type FrecuenciaRepeticion = 'ninguna' | 'semanal' | 'mensual'

export interface ReglaRecurrencia {
  frecuencia: Exclude<FrecuenciaRepeticion, 'ninguna'>
  /** Fecha del último día en que puede caer una ocurrencia. `null` = sin fin. */
  hasta: Date | null
}

export interface Ocurrencia {
  inicio: string
  fin: string | null
}

/**
 * Tope de ocurrencias por ventana. La expansión arranca cerca de la ventana pedida, así que un
 * mes normal genera 4 o 5; esto solo ataja una regla corrupta que no avance nunca.
 */
const MAX_OCURRENCIAS = 200

const FRECUENCIA_A_FREQ: Record<ReglaRecurrencia['frecuencia'], string> = {
  semanal: 'WEEKLY',
  mensual: 'MONTHLY',
}

/**
 * Argentina no tiene horario de verano desde 2009, así que el offset es fijo y no hace falta una
 * librería de zonas para esto.
 */
const OFFSET_ARGENTINA_MS = 3 * 60 * 60 * 1000

const compactar = (fecha: Date): string =>
  fecha.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')

/**
 * `UNTIL` de iCal, en UTC **real**, porque así lo lee Google.
 *
 * El corte es el final del día ARGENTINO elegido: 23:59:59 de acá son las 02:59:59Z del día
 * siguiente. Serializarlo como 23:59:59Z cortaría la serie a las 20:59 hora local y se perdería
 * toda reunión de la noche del último día — que es justo el día que la persona eligió incluir.
 */
const formatearUntil = (hasta: Date): string => {
  const finDelDiaLocal = Date.UTC(
    hasta.getUTCFullYear(), hasta.getUTCMonth(), hasta.getUTCDate(), 23, 59, 59,
  )
  return compactar(new Date(finDelDiaLocal + OFFSET_ARGENTINA_MS))
}

/**
 * Arma el RRULE que va a la base y a Google.
 *
 * El día de la semana y el del mes NO se pasan: los deriva Google de la fecha de inicio, y así se
 * evita la contradicción de una serie "todos los martes" que arranca un jueves.
 */
export const construirRrule = (
  frecuencia: FrecuenciaRepeticion,
  hasta?: string | Date | null,
): string | null => {
  if (frecuencia === 'ninguna') return null

  const partes = [`FREQ=${FRECUENCIA_A_FREQ[frecuencia]}`, 'INTERVAL=1']

  if (hasta) {
    const fin = hasta instanceof Date ? hasta : new Date(`${hasta}T00:00:00Z`)
    if (!Number.isNaN(fin.getTime())) partes.push(`UNTIL=${formatearUntil(fin)}`)
  }

  return `RRULE:${partes.join(';')}`
}

/**
 * Lee el RRULE guardado.
 *
 * Devuelve `null` ante cualquier cosa que no sepa interpretar —regla vacía, `FREQ` desconocido,
 * texto corrupto—. Quien llama trata ese `null` como "evento único": una regla que no se entiende
 * no puede tumbar el calendario entero.
 */
export const parsearRrule = (regla: string | null | undefined): ReglaRecurrencia | null => {
  if (!regla) return null

  const cuerpo = regla.replace(/^RRULE:/i, '')
  const campos = new Map(
    cuerpo
      .split(';')
      .map((parte) => parte.split('='))
      .filter((par): par is [string, string] => par.length === 2)
      .map(([clave, valor]) => [clave.toUpperCase(), valor]),
  )

  const freq = campos.get('FREQ')?.toUpperCase()
  const frecuencia = freq === 'WEEKLY' ? 'semanal' : freq === 'MONTHLY' ? 'mensual' : null
  if (!frecuencia) return null

  return { frecuencia, hasta: parsearUntil(campos.get('UNTIL')) }
}

const parsearUntil = (until: string | undefined): Date | null => {
  if (!until) return null

  // Formato iCal: 20261231T235959Z. Se acepta también la forma corta 20261231.
  const match = /^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})Z?)?$/.exec(until)
  if (!match) return null

  const [, y, m, d, hh, mm, ss] = match
  const enUtcReal = Date.UTC(
    Number(y), Number(m) - 1, Number(d), Number(hh ?? 2), Number(mm ?? 59), Number(ss ?? 59),
  )
  if (Number.isNaN(enUtcReal)) return null

  // El RRULE guarda UTC real (es lo que entiende Google) y todo lo demás de este módulo compara
  // horas de pared. Sin esta resta, el corte quedaría 3 horas corrido hacia adelante.
  return new Date(enUtcReal - OFFSET_ARGENTINA_MS)
}

/**
 * Texto para mostrarle a la persona qué serie es. `null` cuando el evento no se repite, así el
 * llamador decide con eso si dibuja el cartel.
 */
export const describirRecurrencia = (regla: string | null | undefined): string | null => {
  const parsed = parsearRrule(regla)
  if (!parsed) return null

  const base = parsed.frecuencia === 'semanal' ? 'Todas las semanas' : 'Todos los meses'
  if (!parsed.hasta) return base

  const dia = String(parsed.hasta.getUTCDate()).padStart(2, '0')
  const mes = String(parsed.hasta.getUTCMonth() + 1).padStart(2, '0')
  return `${base}, hasta el ${dia}/${mes}/${parsed.hasta.getUTCFullYear()}`
}

/**
 * La ocurrencia n-ésima de una serie mensual, o `null` si ese mes no tiene el día.
 *
 * Se saltea en vez de correrlo al último día del mes: `addMonths` de date-fns llevaría el 31 de
 * enero al 28 de febrero, y Google —que con MONTHLY se guía por el día del mes— no dibuja nada ahí.
 * Con el clamping, Menta mostraría una reunión que en el calendario del estudio no existe.
 */
const ocurrenciaMensual = (base: Date, n: number): Date | null => {
  const dia = base.getUTCDate()
  const fecha = new Date(base)
  fecha.setUTCDate(1)
  fecha.setUTCMonth(fecha.getUTCMonth() + n)

  // Día 0 del mes siguiente = último del actual. En UTC, igual que todo lo demás acá.
  const ultimoDiaDelMes = new Date(
    Date.UTC(fecha.getUTCFullYear(), fecha.getUTCMonth() + 1, 0),
  ).getUTCDate()
  if (dia > ultimoDiaDelMes) return null

  fecha.setUTCDate(dia)
  return fecha
}

const MS_POR_SEMANA = 7 * 24 * 60 * 60 * 1000

/**
 * Índice de la primera ocurrencia que puede caer dentro de la ventana.
 *
 * Sin esto, una serie semanal de hace tres años obliga a iterar 150 veces para dibujar un mes.
 * Se resta 1 para no perder la ocurrencia del borde por redondeo.
 */
const indiceInicial = (inicio: Date, desde: Date, frecuencia: ReglaRecurrencia['frecuencia']): number => {
  if (desde <= inicio) return 0

  const salto =
    frecuencia === 'semanal'
      ? Math.floor((desde.getTime() - inicio.getTime()) / MS_POR_SEMANA)
      : (desde.getUTCFullYear() - inicio.getUTCFullYear()) * 12 +
        (desde.getUTCMonth() - inicio.getUTCMonth())

  return Math.max(0, salto - 1)
}

/**
 * Expande las ocurrencias de un evento dentro de una ventana.
 *
 * Un evento sin regla devuelve su única ocurrencia si cae adentro. La duración se preserva: se
 * calcula del evento base y se le suma a cada inicio, así una reunión de 90 minutos dura lo mismo
 * en todas las semanas.
 */
export const expandirOcurrencias = ({
  fechaInicio,
  fechaFin,
  regla,
  desde,
  hasta,
}: {
  fechaInicio: string
  fechaFin?: string | null
  regla?: string | null
  desde: Date
  hasta: Date
}): Ocurrencia[] => {
  const inicio = new Date(fechaInicio)
  if (Number.isNaN(inicio.getTime())) return []

  const fin = fechaFin ? new Date(fechaFin) : null
  const duracionMs = fin && !Number.isNaN(fin.getTime()) ? fin.getTime() - inicio.getTime() : null

  const armar = (momento: Date): Ocurrencia => ({
    inicio: momento.toISOString(),
    fin: duracionMs === null ? null : new Date(momento.getTime() + duracionMs).toISOString(),
  })

  const parsed = parsearRrule(regla)
  if (!parsed) {
    return inicio >= desde && inicio <= hasta ? [armar(inicio)] : []
  }

  const tope = parsed.hasta && parsed.hasta < hasta ? parsed.hasta : hasta
  const ocurrencias: Ocurrencia[] = []

  let n = indiceInicial(inicio, desde, parsed.frecuencia)

  for (let vueltas = 0; vueltas < MAX_OCURRENCIAS; vueltas += 1, n += 1) {
    const momento =
      parsed.frecuencia === 'semanal'
        // Suma de milisegundos y no `addWeeks`: date-fns mueve el día en hora LOCAL, y sobre un
        // timestamp de pared eso corre la ocurrencia en cualquier runtime con offset distinto.
        ? new Date(inicio.getTime() + n * MS_POR_SEMANA)
        : ocurrenciaMensual(inicio, n)

    // Un mes sin el día pedido no corta la serie: se saltea y se sigue con el siguiente.
    if (momento === null) continue
    if (momento > tope) break
    if (momento >= desde && momento >= inicio) ocurrencias.push(armar(momento))
  }

  return ocurrencias
}
